import { Controller, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { Typography } from '@gto/ui/typography'

import {
  ADDRESS_FIELDS,
  getPayoutFormShape,
  isPayoutMethod,
  isWireRail,
  METHOD_LABELS,
  PAYOUT_METHODS,
  type PayoutFieldSpec,
  RAIL_LABEL_IDS,
  usesBankRail,
  WIRE_RAILS,
  type WireRail,
} from '@/domain/payout-methods'

import { fieldErrorText, SelectField, TextField } from './form'
import type { PayoutMethodForm } from './use-payout-method-form'

// US ACH defaults receiver_type to PERSON on the server; seed it so the common
// case needs no extra pick.
const railDefaults = (rail: WireRail): Record<string, string> =>
  rail === 'US_ACH' ? { receiver_type: 'PERSON' } : {}

type PayoutFieldProps = {
  control: PayoutMethodForm['control']
  field: PayoutFieldSpec
  disabled: boolean
}

const PayoutField = ({ control, field, disabled }: PayoutFieldProps) => {
  const { formatMessage } = useIntl()
  const label = formatMessage({ id: field.labelId })

  return (
    <Controller
      control={control}
      defaultValue=""
      name={`values.${field.name}`}
      render={({ field: rhfField, fieldState }) =>
        field.kind === 'select' ? (
          <SelectField
            disabled={disabled}
            error={fieldErrorText(formatMessage, fieldState.error, 'payout.')}
            label={label}
            name={rhfField.name}
            triggerRef={rhfField.ref}
            value={rhfField.value}
            options={(field.options ?? []).map((opt) => ({
              value: opt.value,
              label: formatMessage({ id: opt.labelId }),
            }))}
            onBlur={rhfField.onBlur}
            onValueChange={rhfField.onChange}
          />
        ) : (
          <TextField
            ref={rhfField.ref}
            disabled={disabled}
            error={fieldErrorText(formatMessage, fieldState.error, 'payout.')}
            label={label}
            name={rhfField.name}
            placeholder={field.placeholder}
            value={rhfField.value}
            inputClassName={
              field.mono === true ? '[&_input]:font-mono [&_input]:text-[0.85rem]' : undefined
            }
            onChange={rhfField.onChange}
          />
        )
      }
    />
  )
}

type PayoutMethodFieldsProps = {
  form: PayoutMethodForm
  disabled?: boolean
}

export const PayoutMethodFields = ({ form, disabled = false }: PayoutMethodFieldsProps) => {
  const { formatMessage } = useIntl()
  const { control, reset } = form

  // `useWatch` (not `form.watch`) so this child re-subscribes and re-renders on
  // its own whenever `method`/`rail` change. Under the React Compiler — active in
  // the browser build (vite.config.ts) — the parent auto-memoizes this element on
  // its stable `form` prop, so a parent re-render no longer refreshes a plain
  // `watch()` read: the derived `shape` would go stale and no detail fields would
  // render after a method is picked (the partner-facing bug this fixes).
  const method = useWatch({ control, name: 'method' })
  const rail = useWatch({ control, name: 'rail' })

  const shape =
    method === ''
      ? { fields: [], includeAddress: false }
      : getPayoutFormShape(method, rail === '' ? null : rail)

  return (
    <div className="flex flex-col gap-3">
      <Controller
        control={control}
        name="method"
        render={({ field, fieldState }) => (
          <SelectField
            disabled={disabled}
            error={fieldErrorText(formatMessage, fieldState.error, 'payout.')}
            label={formatMessage({ id: 'payout.method' })}
            name={field.name}
            triggerRef={field.ref}
            value={field.value}
            options={PAYOUT_METHODS.map((m) => ({
              value: m,
              label: METHOD_LABELS[m],
            }))}
            onBlur={field.onBlur}
            onValueChange={(next) => {
              if (isPayoutMethod(next)) {
                // A method switch is a full re-entry. `reset` (not `setValue`)
                // because a mounted, shared field (e.g. `holder`, kept across
                // rails by its stable React key) is only reliably cleared by a
                // form reset — it also drops the rail and prior field errors.
                reset({ method: next, rail: '', values: {}, usdOnlyConfirmed: false })
              }
            }}
          />
        )}
      />

      {method !== '' && usesBankRail(method) && (
        <Controller
          control={control}
          name="rail"
          render={({ field, fieldState }) => (
            <SelectField
              disabled={disabled}
              error={fieldErrorText(formatMessage, fieldState.error, 'payout.')}
              label={formatMessage({ id: 'payout.rail' })}
              name={field.name}
              triggerRef={field.ref}
              value={field.value}
              options={WIRE_RAILS.map((r) => ({
                value: r,
                label: formatMessage({ id: RAIL_LABEL_IDS[r] }),
              }))}
              onBlur={field.onBlur}
              onValueChange={(next) => {
                if (isWireRail(next)) {
                  // A rail switch reseeds the details (US ACH default) and drops
                  // any value typed under the previous rail + prior field errors.
                  // `reset` (not `setValue`) so a shared mounted field clears.
                  reset({ method, rail: next, values: railDefaults(next), usdOnlyConfirmed: false })
                }
              }}
            />
          )}
        />
      )}

      {shape.fields.map((field) => (
        <PayoutField
          key={field.name}
          control={control}
          disabled={disabled}
          field={field}
        />
      ))}

      {shape.includeAddress && (
        <>
          <Typography
            className="mt-1 text-xs uppercase tracking-[0.06em]"
            color="text.secondary"
            variant="caption"
          >
            {formatMessage({ id: 'payout.address' })}
          </Typography>
          {ADDRESS_FIELDS.map((field) => (
            <PayoutField
              key={field.name}
              control={control}
              disabled={disabled}
              field={field}
            />
          ))}
        </>
      )}
    </div>
  )
}
